import React, { createContext, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { API_BASE } from '../config';

export type ProjectSharePermission = 'reader' | 'commenter' | 'editor' | 'admin';

interface ProjectAccessValue {
  projectId: string | null;
  ownerId: string | null;
  sharePermission: ProjectSharePermission | null;
  isOwner: boolean;
  canViewProject: boolean;
  canEditProject: boolean;
  canManageSharing: boolean;
  isLoading: boolean;
  refresh: () => Promise<void>;
}

const ProjectAccessContext = createContext<ProjectAccessValue | null>(null);

export const ProjectAccessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const params = useParams<{ projectId?: string }>();
  const { token, user } = useAuth();
  const projectId = params.projectId && params.projectId !== 'new' ? params.projectId : null;
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [sharePermission, setSharePermission] = useState<ProjectSharePermission | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(projectId && token));

  const refresh = async (): Promise<void> => {
    if (!token || !projectId) {
      setOwnerId(null);
      setSharePermission(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/projects/${projectId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setOwnerId(data.ownerId || null);
        setSharePermission(data.sharePermission || null);
      } else {
        setOwnerId(null);
        setSharePermission(null);
      }
    } catch (err) {
      console.error('Error fetching project access:', err);
      setOwnerId(null);
      setSharePermission(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, [token, projectId]);

  const isOwner = Boolean(user && ownerId && user.id === ownerId);
  const canViewProject = isOwner || sharePermission !== null;
  const canEditProject = isOwner || sharePermission === 'editor' || sharePermission === 'admin';
  const canManageSharing = isOwner || sharePermission === 'admin';

  const value: ProjectAccessValue = {
    projectId,
    ownerId,
    sharePermission,
    isOwner,
    canViewProject,
    canEditProject,
    canManageSharing,
    isLoading,
    refresh,
  };

  return (
    <ProjectAccessContext.Provider value={value}>
      {children}
    </ProjectAccessContext.Provider>
  );
};

export const useProjectAccess = (): ProjectAccessValue => {
  const context = useContext(ProjectAccessContext);
  if (!context) {
    return {
      projectId: null,
      ownerId: null,
      sharePermission: null,
      isOwner: false,
      canViewProject: false,
      canEditProject: false,
      canManageSharing: false,
      isLoading: false,
      refresh: async () => {},
    };
  }
  return context;
};

export function useOptionalProjectAccess(): ProjectAccessValue | null {
  return useContext(ProjectAccessContext);
}
